import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  FaArrowRight, 
  FaLinkedin,
  FaRocket,
  FaLightbulb,
  FaHandshake,
  FaAward
} from 'react-icons/fa';
import { HiSparkles, HiLightningBolt, HiCube } from 'react-icons/hi';
import './About.css';

const About = () => {
  const founders = [
    {
      name: 'Mayur Sagar',
      initials: 'MS',
      description: 'Strategic visionary passionate about leveraging technology to solve real-world challenges. Dedicated to building solutions that transform businesses and empower teams.',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      linkedin: 'https://www.linkedin.com/in/mayur-sagar-458a41283'
    },
    {
      name: 'Sahil Gite',
      initials: 'SG',
      description: 'Technology innovator with a deep commitment to crafting elegant, scalable solutions. Believes in the power of clean code and thoughtful architecture.',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
      linkedin: 'https://www.linkedin.com/in/sahilgite2003'
    },
    {
      name: 'Anushka Shinde',
      initials: 'AS',
      description: 'Operations strategist focused on delivering excellence through seamless execution. Champions quality, efficiency, and client satisfaction in every project.',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
      linkedin: 'https://www.linkedin.com/in/anushkashinde15'
    }
  ];

  const values = [
    {
      icon: <FaRocket />,
      title: 'Innovation First',
      description: 'We embrace cutting-edge technologies and creative solutions to stay ahead of the curve.'
    },
    {
      icon: <FaLightbulb />,
      title: 'Design Excellence',
      description: 'Every solution we build is crafted with precision, beauty, and user experience in mind.'
    },
    {
      icon: <FaHandshake />,
      title: 'Client Partnership',
      description: 'We work as an extension of your team, fully invested in your success and growth.'
    },
    {
      icon: <FaAward />,
      title: 'Quality Obsessed',
      description: 'We maintain the highest standards in every line of code and every interaction.'
    }
  ];

  return (
    <div className="about-page">
      <Navbar />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-bg">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="section-badge">
              <HiSparkles />
              About Cascade India
            </div>
            <h1 className="hero-title">
              We Build the <span className="gradient-text">Future</span> of Technology
            </h1>
            <p className="hero-description">
              A next-generation technology company dedicated to transforming ambitious ideas 
              into powerful digital realities that drive business growth.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section story-section">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <div className="section-badge">
                <HiCube />
                Our Story
              </div>
              <h2 className="section-title">
                Born from a Vision to <span className="gradient-text">Transform</span>
              </h2>
              <p className="story-paragraph">
                Cascade India was founded with a singular mission: to bridge the gap between 
                cutting-edge technology and real business value. We believe that exceptional 
                software isn't just about code – it's about understanding problems deeply and 
                crafting solutions that make a genuine difference.
              </p>
              <p className="story-paragraph">
                Our team brings together diverse expertise in software engineering, design, 
                and business strategy. This unique blend allows us to approach every project 
                with both technical excellence and strategic insight.
              </p>
              <div className="story-stats">
                <div className="story-stat">
                  <span className="stat-number">500+</span>
                  <span className="stat-label">Projects Delivered</span>
                </div>
                <div className="story-stat">
                  <span className="stat-number">98%</span>
                  <span className="stat-label">Client Satisfaction</span>
                </div>
                <div className="story-stat">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Team Members</span>
                </div>
              </div>
            </div>
            <div className="story-visual">
              <div className="visual-card">
                <div className="card-glow"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section values-section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiLightningBolt />
              Our Values
            </div>
            <h2 className="section-title">
              Principles That <span className="gradient-text">Guide Us</span>
            </h2>
            <p className="section-description">
              Every decision we make is rooted in our core values, ensuring we 
              deliver exceptional results consistently.
            </p>
          </div>

          <div className="values-grid">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="value-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="value-icon">
                  {value.icon}
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="section founders-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiSparkles />
              Leadership
            </div>
            <h2 className="section-title">
              Meet The <span className="gradient-text">Founders</span>
            </h2>
            <p className="section-description">
              The brilliant minds driving innovation and excellence at Cascade India.
            </p>
          </div>

          <div className="founders-grid">
            {founders.map((founder, index) => (
              <div 
                key={index} 
                className="founder-card"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="founder-card-inner">
                  <div 
                    className="founder-avatar"
                    style={{ background: founder.gradient }}
                  >
                    <span className="avatar-initials">{founder.initials}</span>
                    <div className="avatar-glow" style={{ background: founder.gradient }}></div>
                  </div>
                  
                  <div className="founder-info">
                    <h3 className="founder-name">{founder.name}</h3>
                    <p className="founder-description">{founder.description}</p>
                    
                    <div className="founder-social">
                      <a href={founder.linkedin} className="social-link" target="_blank" rel="noopener noreferrer">
                        <FaLinkedin />
                      </a>
                    </div>
                  </div>
                </div>
                <div className="card-border-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-bg">
          <div className="cta-gradient"></div>
        </div>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Ready to Build Something <span className="gradient-text">Amazing</span>?
            </h2>
            <p className="cta-description">
              Let's turn your vision into reality. Get in touch and start your journey with us.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-glow">
                Start a Project
                <FaArrowRight />
              </Link>
              <Link to="/services" className="btn btn-secondary">
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
